// ============ TIPOS DEL SISTEMA DE CALENDARIO ============

export interface TipoServicio {
  id: string;
  empresaId: string;
  
  // Información básica
  nombre: string;
  descripcion?: string;
  categoria?: string;
  
  // Precios
  precio: number;
  precioMin?: number;
  precioMax?: number;
  
  // Configuración
  requiereMateriales: boolean;
  requiereDeposito: boolean;
  porcentajeDeposito?: number;
  
  // Estado
  activo: boolean;
  orden: number;
  
  // Relaciones
  citas?: Cita[];
  
  createdAt: Date;
  updatedAt: Date;
}

export interface ConfiguracionCalendario {
  id: string;
  empresaId: string;
  
  // Horarios del negocio
  horaApertura: string;
  horaCierre: string;
  
  // Días laborales
  trabajaLunes: boolean;
  trabajaMartes: boolean;
  trabajaMiercoles: boolean;
  trabajaJueves: boolean;
  trabajaViernes: boolean;
  trabajaSabado: boolean;
  trabajaDomingo: boolean;
  
  // Capacidad (manual - el dueño decide)
  capacidadDiaria?: string;
  
  // Recordatorios WhatsApp
  recordatorio24h: boolean;
  recordatorio2h: boolean;
  mensajeRecordatorio?: string;
  
  // Políticas
  permiteCancelacion: boolean;
  horasLimiteCancelacion: number;
  
  // Zona horaria
  zonaHoraria: string;
  
  createdAt: Date;
  updatedAt: Date;
}

export interface Cita {
  id: string;
  empresaId: string;
  clienteId: string;
  tipoServicioId?: string;
  empleadoId?: string; // New: Employee assignment
  ubicacionId?: string; // New: Location/Store assignment
  
  // Información de la cita
  titulo: string;
  descripcion?: string;
  
  // Fecha (sin horas específicas)
  fecha: Date;
  ordenDelDia?: number;
  
  // Estado
  estado: EstadoCita;
  
  // Información de contacto
  telefonoContacto?: string;
  whatsapp?: string;
  
  // Recordatorios enviados
  recordatorio24hEnviado: boolean;
  recordatorio2hEnviado: boolean;
  
  // Información comercial
  precio?: number;
  deposito?: number;
  depositoPagado: boolean;
  
  // Notas
  notasInternas?: string;
  materialesNecesarios?: string;
  
  // Ubicación y entrega
  tipoUbicacion: 'TIENDA' | 'DOMICILIO' | 'VIRTUAL'; // New: Store, home delivery, or virtual
  direccionEntrega?: string; // New: Delivery address
  ciudadEntrega?: string; // New: Delivery city
  barrioEntrega?: string; // New: Delivery neighborhood
  instruccionesEntrega?: string; // New: Delivery instructions
  fechaEntrega?: Date;
  entregado: boolean;
  fechaEntregado?: Date;
  
  // Recurrencia
  esRecurrente: boolean;
  frecuenciaRecurrencia?: string;
  fechaProximaCita?: Date;
  citaOriginalId?: string;
  
  // Relaciones
  cliente?: Cliente;
  tipoServicio?: TipoServicio;
  empleado?: Empleado; // New: Employee relation
  ubicacion?: Ubicacion; // New: Location relation
  ventaId?: string;
  
  /** Ruta en Storage (empresa-assets u otro) para foto/comprobante de entrega completada. */
  evidenciaEntregaPath?: string | null;

  /** Factura vinculada (calendario.referencia_factura_id). */
  referenciaFacturaId?: string;
  /** Valor de columna `tipo` en Supabase (p. ej. FACTURA para entregas). */
  calendarTipo?: string;

  // Factura-specific fields
  esFactura?: boolean;
  facturaId?: string;
  numeroFactura?: string;
  tipoEntrega?: 'TIENDA' | 'DOMICILIO';
  horaEntrega?: string;
  facturaItems?: Array<{
    id: string;
    producto: string;
    descripcion: string;
    cantidad: number;
    precioUnitario: number;
    subtotal: number;
  }>;
  
  createdAt: Date;
  updatedAt: Date;
}

export enum EstadoCita {
  PROGRAMADA = 'PROGRAMADA',
  EN_PROCESO = 'EN_PROCESO',
  COMPLETADA = 'COMPLETADA',
  ENTREGADA = 'ENTREGADA',
  CANCELADA = 'CANCELADA',
  NO_ASISTIO = 'NO_ASISTIO',
  REAGENDADA = 'REAGENDADA'
}

// Tipos para el formulario de nueva cita
export interface FormularioNuevaCita {
  clienteId: string;
  tipoServicioId?: string; // Made optional since appointments can be for anything
  empleadoId?: string; // Optional employee assignment
  ubicacionId?: string; // Optional location assignment
  titulo: string;
  descripcion: string;
  fecha: Date;
  telefono: string;
  whatsapp: string;
  precio: string;
  deposito: string;
  materialesNecesarios: string;
  notasInternas: string;
  esRecurrente: boolean;
  frecuenciaRecurrencia: string;
  cantidadRecurrencias: number;
  // Location and delivery fields
  tipoUbicacion: 'TIENDA' | 'DOMICILIO' | 'VIRTUAL';
  direccionEntrega: string;
  ciudadEntrega: string;
  barrioEntrega: string;
  instruccionesEntrega: string;
}

// Tipos para la búsqueda de clientes
export interface Cliente {
  id: string;
  nombre: string;
  apellido?: string;
  telefono?: string;
  email?: string;
  numeroDocumento?: string;
  empresaId: string;
}

// Tipos para empleados
export interface Empleado {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono?: string;
  empresaId: string;
  activo: boolean;
  colorAsignacion: string; // Color for calendar display
  especialidades?: string[]; // Service types they can handle
  createdAt: Date;
  updatedAt: Date;
}

// Tipos para ubicaciones/tiendas
export interface Ubicacion {
  id: string;
  nombre: string;
  direccion: string;
  ciudad: string;
  barrio?: string;
  telefono?: string;
  email?: string;
  empresaId: string;
  activa: boolean;
  capacidadMaxima?: number;
  serviciosDisponibles?: string[]; // Service types available at this location
  horariosAtencion?: {
    lunes?: { apertura: string; cierre: string; };
    martes?: { apertura: string; cierre: string; };
    miercoles?: { apertura: string; cierre: string; };
    jueves?: { apertura: string; cierre: string; };
    viernes?: { apertura: string; cierre: string; };
    sabado?: { apertura: string; cierre: string; };
    domingo?: { apertura: string; cierre: string; };
  };
  createdAt: Date;
  updatedAt: Date;
}

// Tipos para la configuración de recordatorios
export interface ConfiguracionRecordatorios {
  recordatorio24h: boolean;
  recordatorio2h: boolean;
  mensajeRecordatorio: string;
  horaRecordatorio24h: string; // "20:00"
  horaRecordatorio2h: string;  // "08:00"
}

// Tipos para el estado de capacidad
export interface EstadoCapacidad {
  estaAlMaximo: boolean;
  citasProgramadas: number;
  capacidadMaxima?: number;
  notaCapacidad?: string;
  fecha: Date;
}

// Tipos para la vista del calendario
export type VistaCalendario = 'month' | 'week' | 'day';

// Tipos para los eventos del calendario
export interface EventoCalendario {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: Cita;
  style: {
    backgroundColor: string;
    borderRadius: string;
    border: string;
    color: string;
    padding: string;
    fontSize: string;
  };
}

// Tipos para la navegación de fechas
export type DireccionNavegacion = 'prev' | 'next';

// Tipos para el filtrado de citas
export interface FiltrosCitas {
  fecha?: Date;
  estado?: EstadoCita;
  clienteId?: string;
  tipoServicioId?: string;
  rangoFechas?: {
    inicio: Date;
    fin: Date;
  };
}

// Tipos para la creación de citas recurrentes
export interface CitaRecurrente {
  citaBase: Cita;
  frecuencia: string;
  cantidad: number;
  fechaInicio: Date;
}

// Tipos para el seguimiento de recordatorios
export interface RecordatorioEnviado {
  id: string;
  citaId: string;
  tipo: '24h' | '2h';
  fechaEnvio: Date;
  estado: 'ENVIADO' | 'ENTREGADO' | 'LEIDO' | 'ERROR';
  canal: 'WHATSAPP' | 'SMS' | 'EMAIL';
  respuesta?: string;
}
